import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchEvents, fmtDate, fmtRelative, triggerSweep } from "../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import HowItWorks from "../components/HowItWorks";

const TYPE_COLOR = {
  expired: "#EF4444",
  archived: "#F59E0B",
  restored: "#10B981",
};

export default function RetentionPage() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: fetchEvents,
    refetchInterval: 15000,
  });

  const sweep = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await triggerSweep();
      toast.success(`SWEEP_DONE · ${res.expired} expired`);
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (e) {
      toast.error("SWEEP_FAILED", { description: e?.message || "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="retention-page" className="p-4 space-y-3">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-mono text-2xl tracking-tight">RETENTION_LOG</h1>
          <p className="text-zinc-500 text-[12px] mt-1 max-w-2xl leading-relaxed">
            The engine&apos;s memory hygiene. Items live in the active feed for
            30 days, then auto-archive and finally expire — keeping the
            library lean. This page logs every expiration and lets you fire
            a manual sweep if you need to clean up early.
          </p>
        </div>
        <button
          data-testid="retention-sweep-button"
          onClick={sweep}
          disabled={busy}
          className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-2"
        >
          <Trash2 size={12} />
          {busy ? "RUNNING…" : "SWEEP_NOW"}
        </button>
      </div>

      <HowItWorks
        id="retention"
        tag="30-DAY_WINDOW · OWNER_ONLY_SWEEP"
        headline="Items age out automatically. This page is your audit trail for what was archived, expired, or restored — plus a manual sweep button for owners."
        steps={[
          { accent: "emerald", label: "Day 0 → Day 30", body: "Items stay ACTIVE in the live feed and rank in search. Saves to your personal library bypass this clock." },
          { accent: "amber", label: "Day 31 sweep", body: "Items flip to ARCHIVED — out of the live feed but still queryable via the runs log." },
          { accent: "rose", label: "Final expiration", body: "Eventually marked EXPIRED. Cited URLs stay in the source ledger so audits stay intact." },
          { accent: "sky", label: "SWEEP_NOW button", body: "Owners can trigger an immediate retention pass — handy after bulk imports or schema changes." },
          { accent: "purple", label: "REASON column", body: "Tells you why each event fired (e.g. '>30d rolling window', 'innovation cap rotation', 'manual delete')." },
        ]}
      />

      <section className="border border-[#27272A] bg-[#0A0A0A] overflow-x-auto">
        <table className="w-full text-[11px] font-mono min-w-[700px]">
          <thead className="text-zinc-500 bg-[#0E0E0E]">
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-2 uppercase tracking-wider">TIME</th>
              <th className="text-left p-2 uppercase tracking-wider">EVENT</th>
              <th className="text-left p-2 uppercase tracking-wider">TOPIC</th>
              <th className="text-left p-2 uppercase tracking-wider">IDEA_ID</th>
              <th className="text-left p-2 uppercase tracking-wider">REASON</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr
                key={e.id}
                data-testid={`retention-row-${e.id}`}
                className="border-b border-[#161616] hover:bg-[#0E0E0E]"
              >
                <td className="p-2 text-zinc-400" title={fmtDate(e.created_at)}>
                  {fmtRelative(e.created_at)}
                </td>
                <td className="p-2 uppercase">
                  <span
                    className="dot"
                    style={{
                      background: TYPE_COLOR[e.event_type] || "#71717A",
                    }}
                  />
                  {e.event_type}
                </td>
                <td className="p-2 uppercase text-zinc-400">{e.topic_slug || "—"}</td>
                <td className="p-2 text-zinc-500 truncate max-w-[260px]">
                  {e.idea_id}
                </td>
                <td className="p-2 text-zinc-400">{e.reason || "—"}</td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-zinc-500">
                  NO_EVENTS · ideas still inside the 30-day window
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
